import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, ViewChild, ViewEncapsulation, inject, signal } from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatTooltipModule, TooltipPosition } from '@angular/material/tooltip'
import { TranslateModule } from '@ngx-translate/core'
import { NgScrollbar, NgScrollbarModule } from 'ngx-scrollbar'
import { Observable, Subject, Subscription, debounceTime, distinctUntilChanged, filter, from, map, switchMap, takeUntil, tap } from 'rxjs'
import { DirectivesModule } from '../../core/directives/directives.module'

@Component({
    selector: 'app-fc-select-paged',
    templateUrl: './fc-select-paged.component.html',
    styleUrls: ['./fc-select-paged.component.scss', '../../../styles.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.ShadowDom,
    imports: [
        CommonModule,
        DirectivesModule,
        FormsModule,
        MatIconModule,
        MatProgressBarModule,
        MatTooltipModule,
        NgScrollbarModule,
        ReactiveFormsModule,
        TranslateModule,
    ]
})
export class FcSelectPagedComponent implements OnChanges, OnDestroy {
  @Input() public placeholder: string = 'Select an option'
  @Input() public searchPlaceholder: string = ''
  @Input() public service: any
  @Input() public control: any
  @Input() public showImage: boolean = false
  @Input() public fixDropdownDirection: string = ''
  @Input() public searchProperty: string = 'nameContains'
  @Input() public isRequired: boolean = false
  @Input() public fcTooltip: string = ''
  @Input() public fcTooltipDisabled: boolean = true
  @Input() public fcTooltipPosition: TooltipPosition = 'above'

  @ViewChild('optionsList') public optionsList!: ElementRef<HTMLUListElement>
  @ViewChild('inputElement') public inputElement!: ElementRef<HTMLInputElement>

  public showError = signal<boolean>(false)
  public options = signal<any[]>([])
  public isLoading = signal<boolean>(false)
  public isOpen = signal<boolean>(false)
  public isOptionsAbove: boolean = false
  public isOnLastPage: boolean = true
  public shouldLoadMoreItems: boolean = true
  public searchTerm = new FormControl('')

  private elementRef = inject(ElementRef)
  private isCursorPagination: boolean = false
  private searchTermObservable$!: Observable<any>
  private searchTermSubscription!: Subscription
  private readonly cdr = inject(ChangeDetectorRef)
  private readonly destroy$ = new Subject<void>()

  public ngOnChanges(): void {
    if (!this.control || !this.service) {
      return
    }

    this.searchTermObservable$ = this.getSearchTermObservable$()
    this.setSearchTermSubscriptions()
    this.isCursorPagination = Boolean(this.service.after)
    if (!this.isRequired) {
      this.isRequired = this.control?.validator && this.control?.validator({} as FormControl)?.required
    }

    this.control?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      if (!value) {
        this.isOpen.set(false)
      }

      this.cdr.detectChanges()
    })

    this.service.shouldUpdateOptions$?.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setSearchTermSubscriptions()
      this.options.set([])
    })
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()

    if (this.isCursorPagination) {
      this.service.after.set(null)
      return
    }

    this.service?.offset?.update(() => 0)
  }

  public toggleDropdown(): void {
    if (this.control.disabled) {
      return
    }

    if (!this.options().length) {
      this.searchTerm.updateValueAndValidity()
    }

    if (this.fixDropdownDirection === 'up') {
      this.isOptionsAbove = true
      this.isOpen.set(!this.isOpen())
      return
    }

    if (this.fixDropdownDirection === 'down') {
      this.isOptionsAbove = false
      this.isOpen.set(!this.isOpen())
      return
    }

    const componentRect = this.elementRef.nativeElement.getBoundingClientRect()
    const windowHeight = window.innerHeight
    const spaceAbove = componentRect.top
    const spaceBelow = windowHeight - componentRect.bottom
    const optionsListHeight = document.querySelector('.options-list')?.clientHeight || 0

    this.isOptionsAbove = spaceBelow < optionsListHeight || spaceBelow < spaceAbove
    this.isOpen.set(!this.isOpen())

    if (this.isOpen()) {
      setTimeout(() => {
        this.inputElement?.nativeElement.focus()
      }, 0)
    }
  }

  public selectItem(item: any): void {
    this.isOpen.set(false)
    this.control.setValue(item)
    this.control.markAsDirty()
    this.cdr.detectChanges()
  }

  public onScrollbarUpdate(scrollbar: NgScrollbar) {
    scrollbar.scrolled.pipe(takeUntil(this.destroy$)).subscribe((event: any) => {
      this.onScroll(event)
    })
  }

  public onScroll(event: any): void {
    const scrollPosition: number = event.target.scrollTop
    const scrollHeight: number = event.target.scrollHeight
    const clientHeight: number = event.target.clientHeight
    const isAtBottom: boolean = scrollPosition + clientHeight >= scrollHeight

    if (!isAtBottom || this.isLoading() || !this.shouldLoadMoreItems) {
      return
    }

    this.isLoading.set(true)
    if (this.isCursorPagination) {
      this.service.after.set(this.service.pageInfo()?.endCursor)
    } else {
      this.service?.offset.update((value: number) => value + (this.service?.limit() as number))
    }

    from(this.service?.searchPage()).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(async ({ data }: any) => {
      await this.setOptions(data)
      this.isLoading.set(false)
    })
  }

  public async setOptions(data: any): Promise<void> {
    const firstProperty: any = Object.values(data)[0]
    const pageInfo = firstProperty?.pageInfo

    this.service.pageInfo.set(firstProperty?.pageInfo)
    this.isOnLastPage = this.isCursorPagination ? !pageInfo?.startCursor : !pageInfo?.hasNextPage

    if (this.isOnLastPage && !this.options().length) {
      this.options.set(firstProperty?.items)
      return
    }

    this.options.set([...this.options(), ...firstProperty.items])
    this.shouldLoadMoreItems = this.options().length !== firstProperty?.totalCount
  }

  public removeSelectedItem(): void {
    if (this.searchTerm?.value) {
      this.searchTerm.setValue('')
    }

    if (this.control?.value) {
      this.control.setValue(null)
    }
  }

  public trackByFn(index: number, item: string) {
    return item
  }

  @HostListener('document:click', ['$event'])
  public onClick(event: any): void {
    const clickedInside = event.composedPath().includes(this.elementRef.nativeElement)

    if (!clickedInside) {
      this.isOpen.set(false)
    }
  }

  private setSearchTermSubscriptions(): void {
    if (this.searchTermSubscription) {
      this.searchTermSubscription.unsubscribe()
    }

    this.subscribeToSearchTermChanges()
  }

  private subscribeToSearchTermChanges(): void {
    this.searchTermSubscription = this.searchTermObservable$.pipe(takeUntil(this.destroy$)).subscribe(async ({ data }: any) => {
      this.isLoading.set(false)
      await this.setOptions(data)
    })
  }

  private getSearchTermObservable$(): Observable<any> {
    return this.searchTerm.valueChanges
      .pipe(
        distinctUntilChanged(),
        tap(() => { this.isLoading.set(true) }),
        debounceTime(500),
        tap(() => {
          this.options.set([])
          this.isOnLastPage = false

          if (this.isCursorPagination) {
            this.service.after.set(null)
            return
          }

          this.service?.offset.update(() => 0)
        }),
        filter((value) => typeof value === 'string'),
        map((value) => value?.trim().toLowerCase()),
        switchMap(() => {
          this.service?.filterForm?.get(this.searchProperty)?.setValue(this.searchTerm?.value)
          return this.service?.searchPage()
        }),
        takeUntil(this.destroy$)
      )
  }
}
